import React, { useState, useContext, useMemo, useEffect } from "react";
import AdvancedSearchContext from "context/AdvancedSearchContext";
import _ from "lodash";
import styled from "styled-components";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import CustomSwitch from "components/Input/CustomSwitch.js";
import UserService from "services/UserService";
import axios from "axios";
import { getSystemAlerts } from "services/ConfigServices";
import { systemalertstext } from "variables/MBM/systemalerts";
import { criticityData } from "variables/MBM/lists/systemalerts";
import moment from "moment";
import ExportTableToCSV from "components/ExportTableToCSV/ExportTableToCSV";

const UploadButton = styled.div`
  margin-left: 0px;
  &:hover {
    cursor: pointer;
  }
`;

const TableName = styled.h1`
  margin-right: auto;
  margin-left: -8px;
  font-size: 1.3rem;
  font-weight: 500;
`;

export default function useSystemAlertTable() {
  const appContext = useContext(AdvancedSearchContext);
  const { chartIdGroup, groupInfoListById } = appContext;
  const [totalRows, setTotalRows] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const url = getSystemAlerts(chartIdGroup);
  const [results, setResults] = useState({ meters: [] });
  const [loading, setLoading] = useState(false);
  const [alertsData, setalertsData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [hideclosed, setHideclosed] = useState(true);

  const refreshTable = () => {
    setRefresh(!refresh);
  }
  const handleHideclosedChange = (event) => {
    setHideclosed(!hideclosed);
    refreshTable();
  };

  //Ok but should not be able to change group at this level
  let prevIdGroup = ["0"];
  let prevRefresh = true;
  useEffect(() => {
    if (chartIdGroup && chartIdGroup[0] !== "0") {
      if ((prevIdGroup && prevIdGroup === chartIdGroup)
        && (prevRefresh && prevRefresh === refresh)) {
        return;
      }
      //setLoading(true);
      async function fetchData() {
        const response = await axios.get(
          url,
          {
            headers: {
              Authorization: `Bearer ${UserService.getToken()}`,
            },
          }
        );

        setResults(response.data);
        setalertsData(response.data.alerts);
        setLoading(false);
      }
      prevRefresh = refresh;
      prevIdGroup = chartIdGroup;
      fetchData();
    }
  }, [chartIdGroup, refresh]);

  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const SearchComponent = ({ filterText, onFilter, onClear }) => (
    <>
      <div style={{ marginTop: "-7px", marginRight: "10px" }}>
        <TextField
          type="search"
          variant="outlined"
          margin="normal"
          size="small"
          autoFocus="true"
          value={filterText}
          onChange={onFilter}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </div>
    </>
  );

  const filteredItems = _.get(results, `alerts`, []).filter(
    (item) =>
      ((item.id && ("" + item.id).toLowerCase().includes(filterText.toLowerCase())) ||
      (item.itemtype && item.itemtype.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.itemid && item.itemid.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.alerttype && item.alerttype.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.value && item.value.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.criticity && criticityData.find((x) => x.id == item.criticity) && criticityData.find((x) => x.id == item.criticity).name.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.starttimestamp && moment(item.starttimestamp).isValid() && moment(item.starttimestamp).format("DD/MM/YYYY hh:mm:ss").includes(filterText)) ||
      (item.endtimestamp && moment(item.endtimestamp).isValid() && moment(item.endtimestamp).format("DD/MM/YYYY hh:mm:ss").includes(filterText)) ||
      (item.osgroup && groupInfoListById["" + item.osgroup] && groupInfoListById["" + item.osgroup].toLowerCase().includes(filterText.toLowerCase())))
      && 
      (
        !hideclosed || (!item.closed && !item.acquitment?.acquited)
      )
  );

  const subHeaderComponentMemo = useMemo(() => {
    const handleClear = () => {
      if (filterText) {
        setResetPaginationToggle(!resetPaginationToggle);
        setFilterText("");
      }
    };
    const onFilterText = (e) => setFilterText(e.target.value);
    let exportarray = alerts.map((item) => {
      let alertsexport = {};
      alertsexport[systemalertstext.id] = item.id;
      alertsexport[systemalertstext.itemtype] = item.itemtype;
      //alertsexport[systemalertstext.type] = sessiontypeData.find((x) => x.id == item.sessiontype)?.name;
      alertsexport[systemalertstext.alerttype] = item.alerttype;
      alertsexport[systemalertstext.itemid] = item.itemid;
      alertsexport[systemalertstext.group] = groupInfoListById["" + item.osgroup];
      alertsexport[systemalertstext.criticity] = item.criticity;
      alertsexport[systemalertstext.starttimestamp] = (item.starttimestamp && moment(item.starttimestamp).isValid()) ? moment(item.starttimestamp).format("DD/MM/YYYY hh:mm:ss") : "";
      alertsexport[systemalertstext.endtimestamp] = (item.endtimestamp && moment(item.endtimestamp).isValid()) ? moment(item.endtimestamp).format("DD/MM/YYYY hh:mm:ss") : "";
      alertsexport[systemalertstext.value] = item.value;
      alertsexport[systemalertstext.comments] = item.comments;
      alertsexport[systemalertstext.acquitment] = item.acquitment;
      alertsexport[systemalertstext.closed] = item.closed;
      alertsexport[systemalertstext.mailsent] = item.mailsent;
      return alertsexport;
    });
    return (
      <>
        <CustomSwitch
          style={{ margin: "10px 40px 0 3px" }}
          inputTitle={systemalertstext.hideclosed}
          checkedState={hideclosed}
          handleSwitchChange={handleHideclosedChange}
        />
        <SearchComponent
          key="SearchComponent"
          onFilter={onFilterText}
          onClear={handleClear}
          filterText={filterText}
        />
        <UploadButton type="button" onClick={handleClear}>
          <ExportTableToCSV dataarray={exportarray} disabled={!alerts || alerts.length < 1} />
        </UploadButton>
      </>
    );
  }, [filterText, resetPaginationToggle, alertsData, alerts, hideclosed]);

  return {
    gridState: {
      defaultPageSize: 10,
      data: filteredItems,
      paginationTotalRows: totalRows,
      filterable: true,
      manual: true,
      paginationResetDefaultPage: resetPaginationToggle,
      subHeaderComponent: subHeaderComponentMemo,
      loading,
    },
    refetch: refreshTable,
    setAlerts,
  };
}
